import { useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import { FilterableSelect } from '../common/FilterableSelect';
import { CARD_ATTRIBUTE_SCHEMAS, type CardField } from './cardAttributeSchemas';

interface Props {
  elementType: string;
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export function CardAttributeEditor({
  elementType,
  values,
  onChange,
  expanded = false,
  onExpandedChange,
}: Props) {
  const schema = CARD_ATTRIBUTE_SCHEMAS[elementType];
  if (!schema) return null;

  const setValue = useCallback(
    (key: string, value: unknown) => {
      onChange({ ...values, [key]: value });
    },
    [values, onChange],
  );

  const filledCount = schema.filter((f) => {
    const v = values[f.key];
    if (v === undefined || v === null || v === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  }).length;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, ex) => onExpandedChange?.(ex)}
      disableGutters
      elevation={0}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SportsEsportsIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Game Stats</Typography>
          <Chip
            size="small"
            label={`${filledCount}/${schema.length}`}
            variant="outlined"
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2,
          }}
        >
          {schema.map((field) => (
            <CardFieldInput
              key={field.key}
              field={field}
              value={values[field.key]}
              onChange={(val) => setValue(field.key, val)}
            />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function CardFieldInput({
  field,
  value,
  onChange,
}: {
  field: CardField;
  value: unknown;
  onChange: (val: unknown) => void;
}) {
  switch (field.type) {
    case 'number':
      return (
        <TextField
          label={field.label}
          type="number"
          size="small"
          value={value ?? ''}
          onChange={(e) => {
            const num = e.target.value === '' ? '' : Number(e.target.value);
            onChange(num);
          }}
          inputProps={{ min: field.min, max: field.max }}
          InputProps={{
            endAdornment: field.unit ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 0.5 }}
              >
                {field.unit}
              </Typography>
            ) : undefined,
          }}
        />
      );

    case 'select':
      return (
        <FilterableSelect
          label={field.label}
          value={String(value ?? '')}
          onChange={(v) => onChange(v)}
          options={(field.options ?? []).map((o) => ({
            value: o,
            label: o
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase()),
          }))}
          allowEmpty={true}
          emptyLabel="—"
          fullWidth
        />
      );

    case 'multi-select':
      return (
        <Autocomplete
          multiple
          size="small"
          options={field.options ?? []}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={(_, newVal) => onChange(newVal)}
          renderTags={(vals, getTagProps) =>
            vals.map((v, i) => (
              <Chip
                {...getTagProps({ index: i })}
                key={v}
                label={v}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label={field.label} size="small" />
          )}
          sx={{ gridColumn: 'span 2' }}
        />
      );

    case 'tags':
      return (
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={[]}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={(_, newVal) => onChange(newVal)}
          renderTags={(vals, getTagProps) =>
            vals.map((v, i) => (
              <Chip
                {...getTagProps({ index: i })}
                key={v}
                label={v}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.label}
              size="small"
              placeholder="Type and press Enter"
            />
          )}
          sx={{ gridColumn: 'span 2' }}
        />
      );

    case 'text':
    default:
      return (
        <TextField
          label={field.label}
          size="small"
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}
